import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DescriptionIcon from "@material-ui/icons/Description";
import UploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import StatusTag from "./StatusTag";
import { api, fetchBankOptionSets } from "./ProfessionalContacts.helper";
import {
  buildBulkUploadSummary,
  downloadBulkUploadTemplate,
  parseBulkUploadWorkbook,
  markExistingCompanies,
} from "./BulkUpload.workbook";
import { firmTypeLabel } from "../../../../../domain/professionalContacts/professionalContacts.types";
import useStyles from "./ProfessionalContacts.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";

// "Duplicate" is deliberately NOT "expiring" (the Warning tone): a Warning row still imports,
// a Duplicate row does not. Neutral is this feature's established inert tone — the row is valid
// and needs no action from the user, it simply already exists. The emphasis lives in the notice
// above Import and the summary-bar count, not in a louder tag on a row nobody has to fix.
const STATUS_TONE = {
  Ready: "valid",
  Warning: "expiring",
  Error: "danger",
  Duplicate: "neutral",
};

// These rows carry a message and no fieldErrors, so the Details cell must render the message or
// it renders an empty list.
const MESSAGE_ONLY_STATUSES = ["Ready", "Duplicate"];

/**
 * One request per uploaded file (R10), sized to take the whole repository in a single page.
 *
 * GET /professional-firms applies NO server-side pageSize cap — unlike the documents and
 * associated-loans endpoints, which Math.Min(pageSize, 100) — so a large page is honoured
 * verbatim. Confirmed in ProfessionalFirmsController.Search / ProfessionalFirmService.SearchAsync.
 *
 * The response's `total` is what keeps this honest: if it ever exceeds what came back, the marking
 * would be incomplete, so the UI says it could not check rather than showing rows as Ready that
 * the server will then skip. That silent under-report was the open question in HLD §9.1; detecting
 * it is what closes it.
 */
const EXISTING_NAMES_PAGE_SIZE = 1000;

const COULD_NOT_CHECK_NOTICE =
  "Couldn't check which companies already exist, so this table doesn't show them. " +
  "Importing is still safe — the server will skip any that already exist and report them below.";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const BulkUpload = ({ onBack }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [optionSets, setOptionSets] = useState({ bankType: [], country: [] });
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  // Non-blocking: the existing-names lookup failed or came back short, so rows are unmarked.
  // Never an `error` — that would hide the table behind M_DataStateRenderer for a file that
  // parsed perfectly well and can still be imported safely.
  const [existingNamesNotice, setExistingNamesNotice] = useState(null);

  useEffect(() => {
    fetchBankOptionSets().then(setOptionSets);
  }, []);

  const labelMaps = useMemo(
    () => ({
      bankTypeLabelToValue: Object.fromEntries(
        (optionSets.bankType || []).map((o) => [o.label, o.value])
      ),
      countryLabelToValue: Object.fromEntries(
        (optionSets.country || []).map((o) => [o.label, o.value])
      ),
    }),
    [optionSets]
  );

  const handlePick = () => fileInputRef.current?.click();

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setError(null);
    try {
      await downloadBulkUploadTemplate({
        bankTypeOptions: optionSets.bankType,
        countryOptions: optionSets.country,
      });
    } catch (err) {
      setError(err);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const acceptFile = async (selected) => {
    if (!selected) return;
    setFile(selected);
    setSummary(null);
    setImportResult(null);
    setError(null);
    setExistingNamesNotice(null);
    setParsing(true);
    try {
      const buffer = await selected.arrayBuffer();
      const parsed = await parseBulkUploadWorkbook(buffer);
      const parsedSummary = buildBulkUploadSummary(parsed, labelMaps);
      setSummary(await markAgainstRepository(parsedSummary));
    } catch (err) {
      setError(err);
    } finally {
      setParsing(false);
    }
  };

  /**
   * One existing-names request per file (R10), matched in memory. Degrades rather than lies: if
   * the lookup fails, or returns fewer firms than it says exist, the rows are left unmarked and
   * the user is told the table is incomplete. The server still refuses to create duplicates
   * either way, so the data is protected even when the screen cannot show what will be skipped.
   */
  const markAgainstRepository = async (parsedSummary) => {
    try {
      const page = await api.listFirms({
        page: 1,
        pageSize: EXISTING_NAMES_PAGE_SIZE,
      });
      const items = page?.items || [];
      const total = Number.isFinite(Number(page?.total))
        ? Number(page.total)
        : items.length;

      if (items.length < total) {
        setExistingNamesNotice(COULD_NOT_CHECK_NOTICE);
        return parsedSummary;
      }

      // v1.14.0 (FIRM-DUPLICATE-NAMES-015, R12): FirmSummary already carries `type`, so these
      // items are passed straight through — markExistingCompanies is what keys the match on
      // (type, name). No extra request, and the one-lookup-per-file shape (R10) is unaffected.
      return markExistingCompanies(parsedSummary, items);
    } catch {
      setExistingNamesNotice(COULD_NOT_CHECK_NOTICE);
      return parsedSummary;
    }
  };

  const handleFileChange = (event) => acceptFile(event.target.files?.[0]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    acceptFile(event.dataTransfer?.files?.[0]);
  };

  // Duplicates are excluded from the payload as well as flagged. The server skips them regardless
  // (R8) — this keeps the request honest and the two tables telling the same story.
  const validRows = (summary?.rows || []).filter(
    (row) => row.firmUpsert && row.status !== "Duplicate"
  );
  const duplicateCount = summary?.duplicateCount || 0;

  const handleImport = async () => {
    if (!validRows.length) return;
    setImporting(true);
    setError(null);
    try {
      const result = await api.bulkCreateFirms(
        validRows.map((row) => row.firmUpsert)
      );
      setImportResult(result);
    } catch (err) {
      setError(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={clsx(classes.formShell, classes.formShellAuto)}>
      <div className={classes.formTopBar}>
        <button
          type="button"
          aria-label="Back"
          title="Back"
          className={classes.formBackButton}
          onClick={onBack}
        >
          <ArrowBackIcon fontSize="small" />
        </button>
        <Typography component="h3" className={classes.formTitle}>
          Bulk Upload Companies
        </Typography>
        <div className={classes.formTopBarActions}>
          <A_Button
            label="Download Template"
            size="small"
            premiumVariant="secondary"
            startIcon={<GetAppIcon fontSize="small" />}
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
          />
        </div>
      </div>

      <div className={classes.formBody}>
        <input
          ref={fileInputRef}
          type="file"
          accept={`.xlsx,${XLSX_MIME}`}
          hidden
          onChange={handleFileChange}
        />
        <button
          type="button"
          className={clsx(
            classes.dropzone,
            file && classes.dropzoneFilled,
            dragging && classes.dropzoneDragging
          )}
          onClick={handlePick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <DescriptionIcon className={classes.dropzoneIcon} />
          <span className={classes.dropzoneMainText}>
            {file ? (
              file.name
            ) : (
              <>
                Drag the filled-in template here, or{" "}
                <span className={classes.dropzoneBrowse}>browse</span>
              </>
            )}
          </span>
          <span className={classes.dropzoneHint}>
            {file ? "Click to choose a different file" : "Excel (.xlsx) only"}
          </span>
        </button>

        {parsing && <LoaderSkeleton />}

        <M_DataStateRenderer loading={false} error={error} data={summary}>
          {summary && (
            <>
              <div className={classes.sectionHeading}>Validation summary</div>
              <div className={classes.tableContainer}>
                <Table
                  size="small"
                  className={clsx(classes.table, classes.tableWide)}
                >
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Company Reference</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {summary.rows.map((row) => (
                      <TableRow
                        key={`${row.companyReference}-${row.rowNumber}`}
                      >
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.companyReference}</TableCell>
                        <TableCell className={classes.cellStrong}>
                          {row.companyName}
                        </TableCell>
                        <TableCell>
                          {/* row.type is the WIRE value (undefined for an errorRow() row — no
                              Companies row to resolve one from — or an invalid Company Type
                              cell, already flagged as its own field error). Never firmUpsert.type:
                              firmUpsert is null on every Error row, which is exactly the case
                              that needs this fallback to render at all. */}
                          {row.type ? firmTypeLabel(row.type) : "—"}
                        </TableCell>
                        <TableCell>
                          <StatusTag
                            tone={STATUS_TONE[row.status] || "neutral"}
                          >
                            {row.status}
                          </StatusTag>
                        </TableCell>
                        <TableCell>
                          {MESSAGE_ONLY_STATUSES.includes(row.status) ? (
                            row.message
                          ) : (
                            <ul className={classes.errorList}>
                              {(row.fieldErrors || []).map((fe, index) => (
                                <li key={index}>{fe.message}</li>
                              ))}
                            </ul>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className={classes.summaryBar}>
                {summary.readyCount} ready · {summary.warningCount} warning ·{" "}
                {summary.errorCount} error · {duplicateCount} already exist - of{" "}
                {summary.totalRows} compan
                {summary.totalRows === 1 ? "y" : "ies"}.
              </div>

              {existingNamesNotice && (
                <div className={classes.duplicateRowsNotice}>
                  {existingNamesNotice}
                </div>
              )}

              {duplicateCount > 0 && (
                <div className={classes.duplicateRowsNotice}>
                  {duplicateCount} compan{duplicateCount === 1 ? "y" : "ies"} in
                  this file already exist
                  {duplicateCount === 1 ? "s" : ""} in the repository and will
                  not be created.
                </div>
              )}

              {!importResult && (
                <div className={classes.formActionBar}>
                  <A_SecuredAction permission={pageSecurityActions.CREATE}>
                    <A_Button
                      label={`Import ${validRows.length} Valid Compan${
                        validRows.length === 1 ? "y" : "ies"
                      }`}
                      size="small"
                      premiumVariant="primary"
                      startIcon={<UploadIcon fontSize="small" />}
                      onClick={handleImport}
                      disabled={importing || !validRows.length}
                    />
                  </A_SecuredAction>
                </div>
              )}
            </>
          )}
        </M_DataStateRenderer>

        {importing && <LoaderSkeleton />}

        <M_DataStateRenderer loading={false} error={null} data={importResult}>
          {importResult && (
            <>
              <div className={classes.sectionHeading}>Import result</div>
              <div className={classes.tableContainer}>
                <Table
                  size="small"
                  className={clsx(classes.table, classes.tableWide)}
                >
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {(importResult.rows || []).map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell className={classes.cellStrong}>
                          {row.companyName}
                        </TableCell>
                        <TableCell>
                          <StatusTag
                            tone={STATUS_TONE[row.status] || "neutral"}
                          >
                            {row.status}
                          </StatusTag>
                        </TableCell>
                        <TableCell>{row.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className={classes.summaryBar}>
                {importResult.readyCount} ready · {importResult.warningCount}{" "}
                warning · {importResult.errorCount} error ·{" "}
                {importResult.duplicateCount || 0} already existed - of{" "}
                {importResult.totalRows} row
                {importResult.totalRows === 1 ? "" : "s"} sent.
              </div>
            </>
          )}
        </M_DataStateRenderer>
      </div>
    </div>
  );
};

BulkUpload.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default BulkUpload;
